/**
 * Deep-remove `undefined` so Firestore setDoc/updateDoc and OrderDesk JSON
 * never receive undefined keys. Preserves Date and Firestore Timestamp-like objects.
 *
 * By default Firebase FieldValue sentinels (serverTimestamp, etc.) are **kept**
 * so Firestore writes stay valid. Pass `{ dropFieldValues: true }` for OrderDesk
 * JSON bodies so sentinels are not stringified.
 *
 * @param {*} value
 * @param {{ dropFieldValues?: boolean }} [options]
 * @param {number} [depth]
 * @returns {*}
 */
export function stripUndefinedDeep(value, options = {}, depth = 0) {
  const dropFieldValues = Boolean(options && options.dropFieldValues);
  if (depth > 40) return value;
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value !== 'object') return value;
  if (value instanceof Date) return value;
  // Firestore Timestamp (client SDK)
  if (typeof value.toDate === 'function' && typeof value.seconds === 'number') {
    return value;
  }
  // Firebase FieldValue / sentinel (serverTimestamp, deleteField, …)
  if (
    typeof value.isEqual === 'function'
    && (value._methodName != null || value.methodName != null || value.type != null)
  ) {
    return dropFieldValues ? undefined : value;
  }
  if (Array.isArray(value)) {
    return value
      .map((v) => stripUndefinedDeep(v, options, depth + 1))
      .filter((v) => v !== undefined);
  }
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    if (v === undefined) continue;
    const next = stripUndefinedDeep(v, options, depth + 1);
    if (next !== undefined) out[k] = next;
  }
  return out;
}

export default stripUndefinedDeep;
